import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { Grid, Stack } from '@mui/material'
import { ButtonSecondary } from 'components/ButtonSecondary'
import { ItemOption } from 'components/FormInputSelect'
import InputTextEditor from 'components/FormInputTextEditor'
import React, { useCallback, useEffect, useState } from 'react'
import { FieldErrors, FormProvider, useFieldArray, useForm } from 'react-hook-form'
import genericService from 'services/generic.service'
import {
  hearingNarrativesInit,
  hearingNarrativesScheme,
  HearingNarrativesType,
} from 'types/hearing-narratives.type'
import { infractionInit } from 'types/infraction.type'
import Label from '../../../../components/Label'
import { InfractionGroup } from './components/InfractionGroup'
import narrativesService from '../../../../services/narratives.service'
import { useOutletContext, useParams } from 'react-router-dom'
import { isFilled } from 'utils/validate.util'
import { HearingCircumstancesType } from 'types/hearing-circumstances.type'
import { StepIndex } from 'pages/CreateHearing/enums/step.enum'
import { useSnackBar } from 'contexts/snackbar.context'
import { useBackdrop } from 'contexts/backdrop.context'
import { OutletContextType } from 'pages/CreateHearing'
import { zodResolver } from '@hookform/resolvers/zod'
import { SystemMessages } from 'utils/enums/system-message.enum'
import { HttpErrorType } from 'types/http.type'
import { errorColor, errorMessage } from 'utils/error.util'

export const StepNarrativesPage: React.FC = () => {
  const { id } = useParams()
  const { showSnackBar } = useSnackBar()
  const { handleBackdropOpen, handleBackdropClose } = useBackdrop()
  const { formRefs } = useOutletContext<OutletContextType>()

  const form = useForm<HearingNarrativesType>({
    resolver: zodResolver(hearingNarrativesScheme),
    defaultValues: hearingNarrativesInit,
  })

  const {
    control,
    watch,
    setValue,
    reset,
    handleSubmit,
    clearErrors,
    setError,
    formState: { isDirty, dirtyFields },
  } = form

  const hasDirtyFields = Object.keys(dirtyFields).length > 0 && dirtyFields.constructor === Object

  const {
    fields: infractionsFields,
    append: handleAddInfraction,
    remove: handleRemoveInfraction,
  } = useFieldArray({
    control,
    name: 'atoInfracionais',
  })
  const [typeOptions, setTypeOptions] = useState<ItemOption[]>([])

  const infractionsValues = watch('atoInfracionais')

  const fillTypeOptions = useCallback(async (): Promise<void> => {
    try {
      const res = await genericService.get('/tipo-ato-infracional')
      setTypeOptions(res.data)
    } catch (err) {
      console.error('Error request Type Options: ', err)
    }
  }, [setTypeOptions])

  const getNarratives = useCallback(async () => {
    try {
      await narrativesService.getById(id).then((response) => {
        const initialValues = response.data ? response.data : hearingNarrativesInit
        reset(initialValues)
      })
    } catch (err) {
      console.error('Narratives Request: ', err)
    }
  }, [id])

  const lastItem = infractionsValues?.[infractionsValues.length - 1]
  const handleDisableAddButton = lastItem
    ? isFilled(lastItem?.artigo) && isFilled(lastItem?.tipo?.id)
    : true

  useEffect(() => {
    fillTypeOptions()
  }, [fillTypeOptions])

  useEffect(() => {
    getNarratives()
  }, [getNarratives])

  useEffect(() => {
    if (infractionsValues && !infractionsValues.length) {
      setValue('atoInfracionais', [infractionInit], { shouldDirty: false })
    }
  }, [infractionsValues, setValue])

  const saveNarratives = async (data: HearingNarrativesType): Promise<void> => {
    if (id) {
      handleBackdropOpen()
      await narrativesService
        .save(id, data)
        .then(async () => {
          showSnackBar(SystemMessages.MESSAGE_001, 'success')
          await getNarratives()
        })
        .catch((error: HttpErrorType) => {
          showSnackBar(errorMessage(error), errorColor(error))
        })
        .finally(() => handleBackdropClose())
    }
  }

  const onError = (errors: FieldErrors<HearingCircumstancesType>) => {
    clearErrors()
    const recursiveSetError = (errors: FieldErrors<HearingCircumstancesType>, parentPath = '') => {
      Object.entries(errors).forEach(([key, error]: any) => {
        const path = parentPath ? `${parentPath}.${key}` : key

        if (error?.ref) {
          // Se houver uma referência (ref), usamos o nome da referência para definir o erro
          setError(error.ref.name, { type: error.type, message: error.message })
        } else if (typeof error === 'object') {
          // Recursivamente percorre os objetos internos
          recursiveSetError(error, path)
        }
      })
    }
    recursiveSetError(errors)
    console.log('onError', errors)
    return Promise.reject(errors)
  }

  formRefs.current[StepIndex.NARRATIVAS] = {
    submit: async () => {
      console.log('isDirty', isDirty)
      console.log('dirtyFields', dirtyFields)
      console.log('hasDirtyFields', hasDirtyFields)
      if (isDirty && hasDirtyFields) {
        await handleSubmit(saveNarratives, onError)()
      }
      return Promise.resolve()
    },
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(saveNarratives)}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Label
              text='Informado sobre o seu direito constitucional de permanecer em silêncio e cientificado
              do teor da documentação advinda da Delegacia de Polícia, noticiando sobre a prática,
              em tese, do(s) ato(s) infracional(ais) ao(s) crime(s) previsto(s) no(s)'
            />
          </Grid>
          <Grid item xs={12}>
            <Label text='Qual(is) Ato(s) Infracional(ais)?' />
          </Grid>
        </Grid>

        <Grid container spacing={2} rowSpacing={2} sx={{ mt: 1, mb: 1 }}>
          {infractionsFields.map((_, i) => (
            <InfractionGroup
              key={i}
              index={i}
              control={control}
              watch={watch}
              typeOptions={typeOptions}
              onRemove={handleRemoveInfraction}
            />
          ))}
          <Stack sx={{ ml: 2, width: '100%' }}>
            <ButtonSecondary
              maxWidth={185}
              startIcon={<AddRoundedIcon />}
              label='Ato Infracional'
              disabled={!handleDisableAddButton}
              onClick={() => handleAddInfraction(infractionInit)}
            />
          </Stack>
        </Grid>

        <Grid container spacing={2} rowSpacing={4} sx={{ mt: 1, mb: 1 }}>
          <Grid item xs={12}>
            <Label text='O adolescente assim declarou: ' />
          </Grid>
          <Grid item xs={12}>
            <Stack direction='column'>
              <Stack direction='row' sx={{ mb: 1 }}>
                <Label text='Narrativa do adolescente*' sx={{ fontSize: 14 }} />
              </Stack>
              <InputTextEditor control={control} name='narrativaAdolescente' />
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Stack direction='column'>
              <Stack direction='row' sx={{ mb: 1 }}>
                <Label text='Narrativa do representante legal*' sx={{ fontSize: 14 }} />
              </Stack>
              <InputTextEditor control={control} name='narrativaRepLegal' />
            </Stack>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  )
}
